import { formatDayAt } from "@omnabase/ui";
import type { Endpoint, TestCallAnswer } from "./integrations";

/** The site test call round (2026-11-04, brief `docs/briefs/site-test-call-round.md`, its decisions; report
 *  `docs/reviews/site-test-call-round-red-team-report.md`, findings 1, 4, 6 and 7): the status lines of a customer data source's dialog,
 *  pure words from the source's read or a test's answer. "site" for a connected site's dialog, "system" for a company's own system's.
 *  These are status lines (they change with the state), not descriptions. */
export type SourceKind = "site" | "system";

/** the empty list's line, from the source's last answer: from the first failure its reason, else when it last answered, else not yet */
export function lastAnswerLine(e: Pick<Endpoint, "lastOkAt" | "failures" | "lastError">, kind: SourceKind): string {
  if (e.failures >= 1) return `The last call to the ${kind} failed: ${e.lastError ?? "unknown"}.`;
  if (e.lastOkAt) return `The ${kind} answered on ${formatDayAt(e.lastOkAt)} with no customer data.`;
  return `The ${kind} has not answered a customer question yet.`;
}

/** the count of what one answer carried (`answered`, never the learned list): "1 field", "3 fields", or null for none */
export const answeredCount = (answered: string[]): string | null => (answered.length ? `${answered.length} ${answered.length === 1 ? "field" : "fields"}` : null);

/** the line after "Send a test call": the count of this answer, "no customer data for <the presser's address>", or the failure */
export function testOutcomeLine(r: TestCallAnswer, presser: string): string {
  if (!r.ok) return testFailedLine(r.error);
  const n = answeredCount(r.answered);
  return n ? `Answered with ${n}.` : `Answered with no customer data for ${presser}.`;
}
/** a failed test, in the own system's dialog's words; also a refusal such as the hourly limit */
export const testFailedLine = (reason: string): string => `Test call failed · ${reason}`;
/** the own system's toast after a test: "Test call answered 200 OK · N fields" counts this answer */
export const testToastLine = (r: TestCallAnswer): string => (r.ok ? `Test call answered 200 OK · ${r.answered.length} fields` : testFailedLine(r.error));
